import {Module, Store} from "vuex"
import {AppState, RemoteData, RemoteDataError} from "@/store"
import logger from "@/utils/logging"
import apiService from "@/services/api.service"
import {EnabledProductRef, ProductRef, ProductsUpdateBody, ProductUpdate, StoreInfo} from "@/services/models.types"
import {Vue} from "vue-property-decorator"

export interface StoreData {
    info: StoreInfo,
    enabledProducts: EnabledProductRef[]
}

export interface StoreState {
    [storeId: number]: RemoteData<StoreData>
}

export const namespace = "stores"

const getters = {
    IS_STORE_FETCH_IN_PROGRESS: "isStoreFetchInProgress",
}

const actions = {
    FETCH_STORE: "fetchStore",
    FETCH_STORE_ENABLED_PRODUCTS: "fetchStoreEnabledProducts",
    UPDATE_PRODUCTS_STATUS: "persistProduct",
}

const mutations = {
    FETCH_STORE_START: "fetchStoreStart",
    FETCH_STORE_SAVE: "fetchStoreSave",
    FETCH_STORE_ERROR: "fetchStoreError",
    CHANGE_PRODUCT_STATUS: "changeProductStatus",
}

export const module: Module<StoreState, AppState> = {
    namespaced: true,
    state: {},
    getters: {
        [getters.IS_STORE_FETCH_IN_PROGRESS](state: StoreState) {
            return (storeId: number) => {
                const item = state[storeId]
                return item !== undefined && item.isLoading
            }
        },
    },
    mutations: {
        [mutations.FETCH_STORE_START](state: StoreState, storeId: number) {
            const existingItem: RemoteData<StoreData> = state[storeId]
            Vue.set<RemoteData<StoreData>>(state, storeId, {
                isLoading: true,
                error: null,
                data: existingItem === undefined ? undefined : existingItem.data,
            })
        },
        [mutations.FETCH_STORE_SAVE](state: StoreState, item: StoreData) {
            Vue.set<RemoteData<StoreData>>(state, item.info.storeId, {
                isLoading: false,
                error: null,
                data: item,
            })
        },
        [mutations.FETCH_STORE_ERROR](state: StoreState, payload: { storeId: number, error: Error }) {
            const existingItem: RemoteData<StoreData> = state[payload.storeId]
            Vue.set<RemoteData<StoreData>>(state, payload.storeId, {
                isLoading: false,
                error: new RemoteDataError(payload.error),
                data: existingItem.data,
            })
        },
        [mutations.CHANGE_PRODUCT_STATUS](state: StoreState,
                                          payload: { storeId: number, productsUpdate: ProductsUpdateBody }) {
            const existingItem: RemoteData<StoreData> = state[payload.storeId]
            const newEnabledProducts = [...existingItem.data!.enabledProducts]
            payload.productsUpdate.productUpdates.forEach((update: ProductRef) => {
                const itemIndex =
                    newEnabledProducts.findIndex(
                        (item) => item.id === update.id && item.listId === payload.productsUpdate.listId)
                // If the update is to enable a product...
                if (update.isEnabled) {
                    // If the item isn't already enabled, we add it now
                    if (itemIndex < 0) {
                        newEnabledProducts.push(
                            {
                                id: update.id,
                                listId: payload.productsUpdate.listId,
                            })
                    }
                } else {
                    if (itemIndex >= 0) {
                        newEnabledProducts.splice(itemIndex, 1)
                    }
                }
            })

            Vue.set<RemoteData<StoreData>>(state, payload.storeId, {
                isLoading: existingItem.isLoading,
                error: existingItem.error,
                data: {
                    info: existingItem.data!.info,
                    enabledProducts: newEnabledProducts,
                },
            })
        },
    },
    actions: {
        async [actions.FETCH_STORE](context, storeId: number) {
            logger.debug(`Fetching store #${storeId}...`)
            context.commit(mutations.FETCH_STORE_START, storeId)
            try {
                const fetchStoreInfo = apiService.fetchStoreInfo(storeId)
                const fetchStoreEnabledProducts = apiService.fetchStoreEnabledProducts(storeId)
                const storeInfo = await fetchStoreInfo
                logger.debug("Fetched store info:", storeInfo)
                const storeEnabledProducts = await fetchStoreEnabledProducts
                logger.debug("Fetched store enabled products:", storeEnabledProducts)
                context.commit(mutations.FETCH_STORE_SAVE, {info: storeInfo, enabledProducts: storeEnabledProducts})
            } catch (error) {
                logger.error(`Failed to fetch data for store #${storeId}:`, error)
                context.commit(mutations.FETCH_STORE_ERROR, {storeId, error})
            }
        },
        async [actions.FETCH_STORE_ENABLED_PRODUCTS](context, storeId: number) {
            logger.debug(`Fetching enabled products for store #${storeId}...`)
            try {
                const fetchStoreEnabledProducts = apiService.fetchStoreEnabledProducts(storeId)
                const storeEnabledProducts = await fetchStoreEnabledProducts
                logger.debug("Fetched store enabled products:", storeEnabledProducts)
                context.commit(mutations.FETCH_STORE_SAVE, {
                    info: context.state[storeId].data!.info,
                    enabledProducts: storeEnabledProducts,
                })
            } catch (error) {
                logger.error(`Failed to fetch enabled products data for store #${storeId}:`, error)
                context.commit(mutations.FETCH_STORE_ERROR, {storeId, error})
            }
        },
        async [actions.UPDATE_PRODUCTS_STATUS](context, payload: { storeId: number, productsUpdate: ProductsUpdateBody }) {
            context.commit(mutations.CHANGE_PRODUCT_STATUS, payload)
            try {
                await apiService.updateProducts(payload.storeId, payload.productsUpdate)
            } catch (e) {
                logger.error("Failed to persist product update:", e)
                context.dispatch(actions.FETCH_STORE_ENABLED_PRODUCTS, payload.storeId)
                throw e
            }
        },
    },
}

/** Since VueX doesn't currently support fully typing things, we can define the signatures by hand. */
export function wrapGetters(store: Store<AppState>) {
    return {
        isStoreLoading(storeId: number) {
            return store.getters[`${namespace}/${getters.IS_STORE_FETCH_IN_PROGRESS}`](storeId)
        },
    }
}

/** Since VueX doesn't currently support fully typing things, we can define the signatures by hand. */
export function wrapActions(store: Store<AppState>) {
    return {
        loadStore(storeId: number): Promise<void> {
            return store.dispatch(`${namespace}/${actions.FETCH_STORE}`, storeId)
        },
        updateProductsStatus(storeId: number, productsUpdate: ProductsUpdateBody): Promise<void> {
            return store.dispatch(`${namespace}/${actions.UPDATE_PRODUCTS_STATUS}`, {storeId, productsUpdate})
        },
        syncStoreEnabledProducts(storeId: number): Promise<void> {
            return store.dispatch(`${namespace}/${actions.FETCH_STORE_ENABLED_PRODUCTS}`, storeId)
        },
    }
}
